import { SelectOptionType } from '@octano/global-ui';
import { IdentificationSearchFilterValue } from '../../components/IdentificationSearchFilter/types';

export enum StudentWarningsGroupType {
  Academic = 'academic',
  Health = 'health',
  Behavior = 'behavior',
}

export enum StudentWarningsRiskLevel {
  Low = 'low',
  Medium = 'medium',
  High = 'high',
}

export type StudentWarningsSummaryFormData = {
  schools: SelectOptionType[];
  years: SelectOptionType[];
  periods: SelectOptionType[];
  studyPlans: SelectOptionType[];
  campuses: SelectOptionType[];
  warningGroups: SelectOptionType[];
};

export type StudentWarningsSummaryRow = {
  studyPlanEnrollmentId: number;
  name: string;
  identification: string;
  studyPlan: string;
  campus: string;
  promo: string;
  course: string;
  warnings: Record<string, number>;
  riskLevel: StudentWarningsRiskLevel;
  riskLevels: Record<StudentWarningsGroupType, StudentWarningsRiskLevel | null>;
};

export type StudentWarningsSummaryDataParams = {
  warningGroupType: string;
  schoolId?: string;
  periodId?: string;
  year?: number;
  riskLevel?: string;
  studyPlanId?: string;
  campusId?: string;
  name?: string;
  startAt?: string;
  endAt?: string;
};

export type StudentWarningsSummaryDetailsParams = {
  warningGroupType: StudentWarningsGroupType;
  studyPlanEnrollmentId: string;
  periodId: string;
  year: string;
  startAt?: string;
  endAt?: string;
};

export type StudentWarningsSummaryBuildDetailsURLParams = {
  warningGroupType: string;
  periodId: string | number;
  year: string | number;
  startAt?: string;
  endAt?: string;
};

export type StudentWarningsSummaryFormDetailsResponse = {
  warningGroups: SelectOptionType[];
  years: SelectOptionType[];
  periods: SelectOptionType[];
  behaviorRecordSeverities: SelectOptionType[];
  behaviorRecordTypes: SelectOptionType[];
  healthRecordStates: SelectOptionType[];
  healthRecordTypes: SelectOptionType[];
  healthRecordSubTypes: SelectOptionType[];
  behaviorRecordPenalizations: SelectOptionType[];
  behaviorRecordConditionalities: SelectOptionType[];
  behaviorRecordDetentionDays: SelectOptionType[];
};

export type StudentWarningsSummaryDetailsResponse = StudentWarningsSummaryRow & {
  groupData?: any;
};

export type StudentRiskEvaluationSessionsParams = {
  schoolId: string;
  startAt?: string;
  endAt?: string;
};

export type StudentRiskEvaluationSessionParticipant = {
  id: number;
  accountId: number | null;
  email: string;
  name: string;
  account?: {
    id: number;
    name: string;
    email: string;
    lastNames: string;
  };
};

export type StudentRiskEvaluationSession = {
  id: number;
  name: string;
  year: number;
  date: string;
  startAt: string;
  endAt: string;
  schoolId: number;
  periodId: number;
  description: string;
  school?: {
    id: number;
    name: string;
  };
  period?: {
    id: number;
    name: string;
  };
  participants: StudentRiskEvaluationSessionParticipant[];
};

export enum StudentRiskEvaluationEnrollmentState {
  Active = 'active',
  Inactive = 'inactive',
}

export type StudentRiskEvaluationSessionEnrollment = {
  id: number;
  sessionId: number;
  studyPlanEnrollmentId: number;
  prevSessionEnrollmentId: number | null;
  calculatedRiskLevel: StudentWarningsRiskLevel;
  definedRiskLevel: StudentWarningsRiskLevel | null;
  definedAt: string | null;
  warningsCount: number;
  riskLevels: Record<StudentWarningsGroupType, StudentWarningsRiskLevel | null>;
  warningsCounts: Record<StudentWarningsGroupType, number>;
  keepForNextSession: boolean;
  conditionality: boolean | null;
  state: StudentRiskEvaluationEnrollmentState | null;
  notes: string | null;
  name: string;
  identification: string;
  createdAt: Date | null;
  session?: StudentRiskEvaluationSession;
};

export type StudentRiskEvaluationSessionParams = {
  sessionId: string;
  riskLevel?: string;
  definedRiskLevel?: string;
  name?: string;
  identification?: Partial<IdentificationSearchFilterValue>;
  group?: unknown;
  promo?: unknown;
};

export type StudentRiskActionRecords = {
  id: number;
  actionId: number;
  adminAccountId: number;
  notes: string;
  createdAt: Date | null;
  updatedAt: Date | null;
  adminAccount?: unknown;
};

export type StudentRiskAction = {
  id: number;
  groupType: StudentWarningsGroupType;
  actionType: string;
  studentId: number;
  sessionId: number;
  adminAccountId: number;
  notes: string;
  owner: string;
  createdAt: Date | null;
  updatedAt: Date | null;
  student?: unknown;
  adminAccount?: unknown;
  session?: StudentRiskEvaluationSession;
  records?: StudentRiskActionRecords[];
};
